import { firebaseAuth } from '@/lib/firebase';
import { signOut } from 'firebase/auth';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
export const PORTAL_URL = import.meta.env.VITE_PORTAL_URL ?? 'http://localhost:3002';

// ── Impersonation helpers ──────────────────────────────

export const IMPERSONATION_KEY = 'enska_support_token';

export function getImpersonationToken(): string | null {
  return sessionStorage.getItem(IMPERSONATION_KEY);
}
export function setImpersonationToken(token: string) {
  sessionStorage.setItem(IMPERSONATION_KEY, token);
}
export function clearImpersonationToken() {
  sessionStorage.removeItem(IMPERSONATION_KEY);
}
export function isImpersonating(): boolean {
  return !!getImpersonationToken();
}

// ── Auth kick-out on expired / revoked session ───────

function handleAuthFailure() {
  signOut(firebaseAuth).catch(() => {});
  sessionStorage.removeItem('enska_support_token'); // clear impersonation too
  window.location.replace('/login');
}

// ── Token helper ─────────────────────────────────────

async function getToken(): Promise<string> {
  // Support session takes priority over Firebase auth
  const impToken = getImpersonationToken();
  if (impToken) return impToken;
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

// ── Core fetch wrapper ───────────────────────────────

interface ApiOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  /** Override the auth token (e.g. impersonation token) */
  token?: string;
  /** Skip auth header (for public endpoints) */
  public?: boolean;
  /** Return raw text instead of JSON */
  asText?: boolean;
}

export async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { token, public: isPublic, headers: extraHeaders, ...rest } = options;

  const authHeader: Record<string, string> = isPublic
    ? {}
    : { Authorization: `Bearer ${token ?? (await getToken())}` };

  const hasBody = rest.body !== undefined && rest.body !== null;

  const res = await fetch(`${API_BASE}${path}`, {
    cache: 'no-store',
    ...rest,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      'x-enska-portal': 'tenant',
      ...authHeader,
      ...extraHeaders,
    },
  });

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) { handleAuthFailure(); }
    const error = await res.json().catch(() => ({ message: res.statusText })) as Record<string, unknown>;
    const message = (error.message as string) ?? res.statusText;
    const resolutionHint = error.resolutionHint as string | undefined;
    throw new ApiError(res.status, message, error, resolutionHint);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return res.json();
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
    public readonly resolutionHint?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Auth ─────────────────────────────────────────────

export const authApi = {
  me: () => apiFetch<Record<string, unknown>>('/v1/me'),
};

// ── Health ───────────────────────────────────────────

export const healthApi = {
  check: () => apiFetch<{ status: string; db: string; redis: string; version: string }>('/health', { public: true }),
};

// ── Admin — Tenants ───────────────────────────────────

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  plan: 'TRIAL' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  firebaseProjectId: string | null;
  customDomain: string | null;
  settings: Record<string, unknown>;
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantDetail extends Tenant {
  users: TenantUser[];
}

export interface TenantUser {
  id: string;
  tenantId: string;
  email: string;
  name: string | null;
  role: string;
  isSupportAccount: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface CreateTenantPayload {
  name: string;
  slug: string;
  plan?: string;
}

export interface ImpersonationToken {
  token: string;
  expiresAt: string;
  supportAccountEmail: string;
  note: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export const adminApi = {
  tenants: {
    list: (params?: { page?: number; limit?: number; status?: string }) => {
      const qs = new URLSearchParams();
      if (params?.page !== undefined) qs.set('page', String(params.page));
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.status) qs.set('status', params.status);
      const query = qs.toString();
      return apiFetch<PaginatedResponse<Tenant>>(`/v1/admin/tenants${query ? `?${query}` : ''}`);
    },

    get: (tenantId: string) =>
      apiFetch<TenantDetail>(`/v1/admin/tenants/${tenantId}`),

    create: (payload: CreateTenantPayload) =>
      apiFetch<Tenant>('/v1/admin/tenants', { method: 'POST', body: JSON.stringify(payload) }),

    update: (tenantId: string, payload: Partial<CreateTenantPayload>) =>
      apiFetch<Tenant>(`/v1/admin/tenants/${tenantId}`, { method: 'PATCH', body: JSON.stringify(payload) }),

    impersonate: (tenantId: string) =>
      apiFetch<ImpersonationToken>(`/v1/admin/tenants/${tenantId}/impersonate`, { method: 'POST' }),
  },
};

// ── Phase 2: Agents ───────────────────────────────────

export type AgentStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
export type ChannelType = 'WEB_CHAT' | 'API' | 'EMAIL' | 'WHATSAPP' | 'SMS' | 'VOICE';
export type LlmProvider = 'GOOGLE_GEMINI' | 'OPENAI' | 'ANTHROPIC';
export type ModelUseCase = 'CONVERSATION' | 'ROUTING' | 'SUMMARIZATION' | 'EMBEDDING';

export interface Agent {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  status: AgentStatus;
  routingCountries: string[];
  routingSourceApps: string[];
  routingChannels: ChannelType[];
  routingPriority: number;
  defaultModelProviderId: string | null;
  createdAt: string;
  updatedAt: string;
  orchestrationConfig: OrchestrationConfig | null;
  _count?: { conversations: number };
  active?: boolean;
}

export interface OrchestrationConfig {
  id: string;
  agentId: string;
  systemPrompt: string | null;
  maxTurnsBeforeEscalation: number;
}

export interface ModelProviderConfig {
  id: string;
  tenantId: string | null;
  provider: LlmProvider;
  modelName: string;
  useCase: ModelUseCase;
  costTier: string;
  latencyTier: string;
  priority: number;
  enabled: boolean;
  encryptedKey: string | null;
  createdAt: string;
}

export const agentApi = {
  list: () => apiFetch<Agent[]>('/v1/agents'),
  get: (id: string) => apiFetch<Agent>(`/v1/agents/${id}`),
  create: (data: { name: string; description?: string; routingChannels?: ChannelType[]; routingPriority?: number }) =>
    apiFetch<Agent>('/v1/agents', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; description: string; status: AgentStatus; routingChannels: ChannelType[]; routingPriority: number; defaultModelProviderId: string }>) =>
    apiFetch<Agent>(`/v1/agents/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  archive: (id: string) => apiFetch<Agent>(`/v1/agents/${id}`, { method: 'DELETE' }),
  upsertOrchestration: (id: string, data: { systemPrompt?: string; maxTurnsBeforeEscalation?: number }) =>
    apiFetch<OrchestrationConfig>(`/v1/agents/${id}/orchestration-config`, { method: 'POST', body: JSON.stringify(data) }),
};

export const modelProviderApi = {
  list: () => apiFetch<ModelProviderConfig[]>('/v1/agents/model-providers/list'),
  upsert: (data: { id?: string; provider: LlmProvider; modelName: string; useCase: ModelUseCase; priority?: number; enabled?: boolean; apiKey?: string }) =>
    apiFetch<ModelProviderConfig>('/v1/agents/model-providers', { method: 'POST', body: JSON.stringify(data) }),
  delete: (configId: string) => apiFetch<void>(`/v1/agents/model-providers/${configId}`, { method: 'DELETE' }),
  verifyKey: (data: { provider: LlmProvider; apiKey: string; modelName: string }) =>
    apiFetch<{ valid: boolean; latencyMs: number; error?: string; model?: string }>('/v1/agents/model-providers/verify', { method: 'POST', body: JSON.stringify(data) }),
};

// ── Phase 2: Conversations ────────────────────────────

export type ConversationStatus = 'OPEN' | 'WAITING_FOR_USER' | 'WAITING_FOR_AGENT' | 'RESOLVED' | 'ABANDONED';
export type SenderType = 'USER' | 'AGENT' | 'SYSTEM' | 'OPERATOR';
export type ConversationEventType =
  | 'CONVERSATION_STARTED' | 'MESSAGE_RECEIVED' | 'MODEL_REQUEST_STARTED'
  | 'MODEL_REQUEST_COMPLETED' | 'MODEL_REQUEST_FAILED' | 'MESSAGE_SENT'
  | 'HANDOFF_CREATED' | 'HANDOFF_RESOLVED' | 'CONVERSATION_RESOLVED' | 'CONVERSATION_ABANDONED'
  | 'SKILL_SELECTED' | 'SKILL_EXECUTION_STARTED' | 'SKILL_EXECUTION_COMPLETED' | 'SKILL_EXECUTION_FAILED'
  | 'SKILL_APPROVAL_REQUESTED' | 'SKILL_APPROVAL_APPROVED' | 'SKILL_APPROVAL_REJECTED' | 'SKILL_APPROVAL_TIMED_OUT'
  | 'GUARDRAIL_TRIGGERED';

export interface Conversation {
  id: string;
  tenantId: string;
  agentId: string | null;
  agent: { id: string; name: string } | null;
  channel: ChannelType;
  sourceApp: string;
  status: ConversationStatus;
  turnCount: number;
  externalUserId: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { messages: number };
}

export interface ConversationDetail extends Conversation {
  messages: ConversationMessage[];
  events: ConversationEvent[];
  handoff: Handoff | null;
}

export interface ConversationMessage {
  id: string;
  senderType: SenderType;
  content: string;
  createdAt: string;
}

export interface ConversationEvent {
  id: string;
  eventType: ConversationEventType;
  actor: string;
  sequenceNumber: number;
  payload: Record<string, unknown> | null;
  durationMs: number | null;
  costUnits: number | null;
  createdAt: string;
}

export interface HandoffMessage {
  id: string;
  senderType: string;
  content: string;
  createdAt: string;
}

export interface Handoff {
  id: string;
  conversationId: string;
  status: 'OPEN' | 'ASSIGNED' | 'RESOLVED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  note: string | null;
  createdAt: string;
  assignedToId: string | null;
  assignedTo?: { id: string; name: string | null; email: string } | null;
  /** Embedded conversation (returned by list endpoint) */
  conversation?: {
    id: string;
    channel: string;
    externalUserId: string | null;
    messages: HandoffMessage[];
  };
}

export const conversationApi = {
  list: (params?: { status?: ConversationStatus; agentId?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.agentId) qs.set('agentId', params.agentId);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return apiFetch<Conversation[]>(`/v1/conversations${q ? `?${q}` : ''}`);
  },
  get: (id: string) => apiFetch<ConversationDetail>(`/v1/conversations/${id}`),
  start: (data: { agentId?: string; initialMessage?: string; channel?: ChannelType }) =>
    apiFetch<{ conversation: Conversation; agentMessage: string | null }>('/v1/conversations', { method: 'POST', body: JSON.stringify(data) }),
  sendMessage: (id: string, content: string) =>
    apiFetch<{ agentMessage: string; handoffCreated: boolean }>(`/v1/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify({ content }) }),
  updateStatus: (id: string, status: ConversationStatus) =>
    apiFetch<Conversation>(`/v1/conversations/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};

// ── Tenant API Keys ────────────────────────────────────

export interface TenantApiKeyRecord {
  id: string;
  name: string;
  description: string | null;
  keyPrefix: string;
  scopes: string[];
  allowedIps: string[];
  role: string;
  agentId: string | null;
  sourceApp: string;
  rateLimitPerMinute: number;
  usageCount: number;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  active: boolean;
  expiresAt: string | null;
  createdAt: string;
}

export interface ApiKeyScope {
  scope: string;
  description: string;
}

export const tenantApiKeyApi = {
  list: () => apiFetch<TenantApiKeyRecord[]>('/v1/settings/api-keys'),
  listScopes: () => apiFetch<ApiKeyScope[]>('/v1/settings/api-keys/scopes'),
  create: (data: {
    name: string;
    description?: string;
    scopes: string[];
    role?: string;
    expiresAt?: string;
    allowedIps?: string[];
    rateLimitPerMinute?: number;
    sourceApp?: string;
    agentId?: string;
  }) =>
    apiFetch<{ key: string; rawKey: string } & TenantApiKeyRecord>('/v1/settings/api-keys', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; description: string; scopes: string[]; allowedIps: string[]; role: string; expiresAt: string | null; rateLimitPerMinute: number; active: boolean }>) =>
    apiFetch<TenantApiKeyRecord>(`/v1/settings/api-keys/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  rotate: (id: string) =>
    apiFetch<{ rawKey: string }>(`/v1/settings/api-keys/${id}/rotate`, { method: 'POST' }),
  revoke: (id: string) => apiFetch<void>(`/v1/settings/api-keys/${id}`, { method: 'DELETE' }),
};

// Legacy alias
export type ChatApiKey = TenantApiKeyRecord;
export const chatApiKeyApi = tenantApiKeyApi;

// ── Channels (inbound connections) ──────────────────────

export type AuthMode = 'PUBLIC' | 'OPTIONAL' | 'AUTHENTICATED' | 'IDENTITY';

export interface Channel {
  id: string;
  name: string;
  description: string | null;
  sourceApp: string;
  type: ChannelType;
  agentId: string | null;
  authMode: AuthMode;
  authConfig: { jwksUrl?: string; jwtIssuer?: string; jwtAudience?: string } | null;
  defaultSessionContext: Record<string, unknown> | null;
  allowedOrigins: string[];
  allowedCountries: string[];
  allowedIps: string[];
  rateLimitPerMinute: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  apiKey?: { keyPrefix: string; active: boolean; usageCount: number; lastUsedAt: string | null } | null;
}

export const channelApi = {
  list: () => apiFetch<Channel[]>('/v1/settings/channels'),
  get: (id: string) => apiFetch<Channel>(`/v1/settings/channels/${id}`),
  create: (data: {
    name: string;
    description?: string;
    sourceApp?: string;
    type?: ChannelType;
    agentId?: string;
    authMode?: AuthMode;
    authConfig?: { jwksUrl?: string; jwtIssuer?: string; jwtAudience?: string };
    defaultSessionContext?: Record<string, unknown>;
    allowedOrigins?: string[];
    rateLimitPerMinute?: number;
  }) =>
    apiFetch<Channel & { key: string; channelKey: string }>('/v1/settings/channels', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{
    name: string;
    description: string;
    agentId: string | null;
    authMode: AuthMode;
    authConfig: { jwksUrl?: string; jwtIssuer?: string; jwtAudience?: string } | null;
    defaultSessionContext: Record<string, unknown> | null;
    allowedOrigins: string[];
    allowedCountries: string[];
    allowedIps: string[];
    rateLimitPerMinute: number;
    active: boolean;
  }>) =>
    apiFetch<Channel>(`/v1/settings/channels/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  rotateKey: (id: string) =>
    apiFetch<{ channelKey: string }>(`/v1/settings/channels/${id}/rotate-key`, { method: 'POST' }),
  delete: (id: string) => apiFetch<void>(`/v1/settings/channels/${id}`, { method: 'DELETE' }),
};

// Legacy aliases
export type ChatApp = Channel;
export const chatAppApi = channelApi;

// ── Phase 2: Handoffs ─────────────────────────────────

export const handoffApi = {
  list: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return apiFetch<Handoff[]>(`/v1/handoffs${q}`);
  },
  create: (conversationId: string, note?: string, priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT') =>
    apiFetch<Handoff>('/v1/handoffs', { method: 'POST', body: JSON.stringify({ conversationId, note, priority }) }),
  sendMessage: (handoffId: string, content: string) =>
    apiFetch<ConversationMessage>(`/v1/handoffs/${handoffId}/message`, { method: 'POST', body: JSON.stringify({ content }) }),
  resolve: (handoffId: string) =>
    apiFetch<Handoff>(`/v1/handoffs/${handoffId}/resolve`, { method: 'POST' }),
  release: (handoffId: string) =>
    apiFetch<Handoff>(`/v1/handoffs/${handoffId}/release`, { method: 'POST' }),

  claim: (handoffId: string) =>
    apiFetch<Handoff>(`/v1/handoffs/${handoffId}/claim`, { method: 'POST' }),
  assign: (handoffId: string, operatorId?: string) =>
    apiFetch<Handoff>(`/v1/handoffs/${handoffId}/assign`, { method: 'POST', body: JSON.stringify({ operatorId }) }),
  assist: (handoffId: string) =>
    apiFetch<{ draft: string | null; kbChunksUsed: number }>(`/v1/handoffs/${handoffId}/assist`, { method: 'POST' }),
};


// ════════════════════════════════════════════════════════
// Phase 3 Types
// ════════════════════════════════════════════════════════

export interface Skill {
  id: string;
  name: string;
  description?: string;
  implementationType: 'KNOWLEDGE_RETRIEVAL' | 'EXTERNAL_API' | 'CODE' | 'PROMPT_ONLY';
  toolName?: string;
  inputSchema?: object;
  outputSchema?: object;
  skillConfig?: object;
  authRequirement: 'NONE' | 'OPTIONAL' | 'REQUIRED';
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  approvalRequired: boolean;
  approvalRoles: string[];
  maxExecutionsPerConversation?: number;
  maxExecutionsPerHour?: number;
  countryRestrictions: string[];
  systemPromptInstructions?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  agentConfigs?: AgentSkillConfig[];
  _count?: { agentConfigs: number };
}

export interface AgentSkillConfig {
  id: string;
  agentId: string;
  skillId: string;
  enabled: boolean;
  approvalRequired?: boolean;
  sortOrder: number;
  skill: Skill;
  agent?: { id: string; name: string; status: string };
}

export interface Integration {
  id: string;
  name: string;
  type: 'REST_API' | 'GRAPHQL';
  active: boolean;
  config: {
    baseUrl: string;
    timeout?: number;
    authType?: 'NONE' | 'BEARER' | 'API_KEY' | 'BASIC';
    authValue?: string;
    apiKeyHeader?: string;
  };
  createdAt: string;
  _count?: { toolDefinitions: number };
}

export interface ToolDefinition {
  id: string;
  toolName: string;
  displayName: string;
  description: string;
  executorType: 'HTTP' | 'CODE' | 'DB_QUERY';
  integrationId?: string;
  integration?: Integration;
  httpConfig?: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    path: string;
    headers?: Record<string, string>;
  };
  sandboxCode?: string;
  inputSchema?: object;
  outputSchema?: object;
  contextMapping?: Record<string, unknown>;
  active: boolean;
  operatorVisible: boolean;
  operatorLabel?: string | null;
  createdAt: string;
}

export interface KnowledgeTopic {
  id: string;
  name: string;
  sourceId: string;
  parentId?: string;
  children?: KnowledgeTopic[];
  articles?: KnowledgeArticle[];
}

export interface KnowledgeSource {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  createdAt: string;
  _count?: { articles: number; topics: number };
}

export interface KnowledgeArticle {
  id: string;
  sourceId: string;
  topicId?: string;
  title: string;
  content: string;
  country: string | null;  // ISO 3166-1 alpha-2, null = global
  language: string;        // ISO 639-1
  published: boolean;
  indexedAt?: string;
  createdAt: string;
  updatedAt: string;
  topic?: { id: string; name: string };
  _count?: { chunks: number };
}

export interface ApprovalRequest {
  id: string;
  conversationId: string;
  skillId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'TIMED_OUT';
  userMessage?: string;
  expiresAt: string;
  resolvedBy?: string;
  resolvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
}

export interface Guardrail {
  id: string;
  name: string;
  description?: string;
  ruleType: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  pattern?: string;
  agentIds: string[];
  active: boolean;
  createdAt: string;
}

// ── Phase 3: Skills ───────────────────────────────────

export const skillApi = {
  list: () => apiFetch<Skill[]>('/v1/skills'),
  get: (id: string) => apiFetch<Skill>(`/v1/skills/${id}`),
  create: (body: Partial<Skill>) => apiFetch<Skill>('/v1/skills', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Skill>) => apiFetch<Skill>(`/v1/skills/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  archive: (id: string) => apiFetch<void>(`/v1/skills/${id}`, { method: 'DELETE' }),
  agentSkills: (agentId: string) => apiFetch<AgentSkillConfig[]>(`/v1/agents/${agentId}/skills`),
  attachSkill: (agentId: string, skillId: string) =>
    apiFetch<AgentSkillConfig>(`/v1/agents/${agentId}/skills`, { method: 'POST', body: JSON.stringify({ skillId }) }),
  updateAgentSkillConfig: (agentId: string, skillId: string, data: { enabled?: boolean; approvalRequired?: boolean }) =>
    apiFetch<AgentSkillConfig>(`/v1/agents/${agentId}/skills/${skillId}`, { method: 'PUT', body: JSON.stringify(data) }),
  detachSkill: (agentId: string, skillId: string) =>
    apiFetch<void>(`/v1/agents/${agentId}/skills/${skillId}`, { method: 'DELETE' }),
};

// ── Phase 3: Tools & Integrations ────────────────────

export const integrationApi = {
  list: () => apiFetch<Integration[]>('/v1/integrations'),
  create: (body: { name: string; type?: string; baseUrl: string; timeout?: number; authType?: string; authValue?: string; apiKeyHeader?: string }) =>
    apiFetch<Integration>('/v1/integrations', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: object) =>
    apiFetch<Integration>(`/v1/integrations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/integrations/${id}`, { method: 'DELETE' }),
};

export const toolApi = {
  list: () => apiFetch<ToolDefinition[]>('/v1/tools'),
  operatorActions: () => apiFetch<ToolDefinition[]>('/v1/tools?operatorVisible=true'),
  create: (body: Partial<ToolDefinition>) => apiFetch<ToolDefinition>('/v1/tools', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: object) => apiFetch<ToolDefinition>(`/v1/tools/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/tools/${id}`, { method: 'DELETE' }),
  test: (id: string, params: Record<string, unknown>) =>
    apiFetch<unknown>(`/v1/tools/${id}/test`, { method: 'POST', body: JSON.stringify(params) }),
  audits: () => apiFetch<unknown[]>('/v1/tools/audit'),
};

// ── Agent-Tool direct assignment ──────────────────────

export interface AgentToolConfig {
  id: string;
  tenantId: string;
  agentId: string;
  toolId: string;
  enabled: boolean;
  instructions: string | null;
  defaultParams: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  tool: Pick<ToolDefinition, 'id' | 'toolName' | 'displayName' | 'description' | 'executorType' | 'active' | 'inputSchema'>;
}

export const agentToolApi = {
  list: (agentId: string) =>
    apiFetch<AgentToolConfig[]>(`/v1/agents/${agentId}/tools`),
  attach: (agentId: string, toolId: string) =>
    apiFetch<AgentToolConfig>(`/v1/agents/${agentId}/tools`, { method: 'POST', body: JSON.stringify({ toolId }) }),
  update: (agentId: string, toolId: string, body: { enabled?: boolean; instructions?: string | null; defaultParams?: object | null }) =>
    apiFetch<AgentToolConfig>(`/v1/agents/${agentId}/tools/${toolId}`, { method: 'PUT', body: JSON.stringify(body) }),
  detach: (agentId: string, toolId: string) =>
    apiFetch<void>(`/v1/agents/${agentId}/tools/${toolId}`, { method: 'DELETE' }),
};

// ── Phase 3: Knowledge ────────────────────────────────

export const knowledgeApi = {
  listSources: () => apiFetch<KnowledgeSource[]>('/v1/knowledge/sources'),
  getSource: (id: string) => apiFetch<KnowledgeSource>(`/v1/knowledge/sources/${id}`),
  createSource: (body: { name: string; description?: string }) =>
    apiFetch<KnowledgeSource>('/v1/knowledge/sources', { method: 'POST', body: JSON.stringify(body) }),
  updateSource: (id: string, body: object) =>
    apiFetch<KnowledgeSource>(`/v1/knowledge/sources/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  listLocales: (sourceId: string) =>
    apiFetch<Array<{ country: string | null; language: string; count: number }>>(`/v1/knowledge/sources/${sourceId}/locales`),
  listArticles: (sourceId: string, opts?: { country?: string; language?: string; published?: boolean; topicId?: string }) => {
    const params = new URLSearchParams();
    if (opts?.country) params.set('country', opts.country);
    if (opts?.language) params.set('language', opts.language);
    if (opts?.published !== undefined) params.set('published', String(opts.published));
    if (opts?.topicId) params.set('topicId', opts.topicId);
    const q = params.toString() ? `?${params.toString()}` : '';
    return apiFetch<KnowledgeArticle[]>(`/v1/knowledge/sources/${sourceId}/articles${q}`);
  },
  createArticle: (sourceId: string, body: { title: string; content: string; topicId?: string; country?: string | null; language?: string }) =>
    apiFetch<KnowledgeArticle>(`/v1/knowledge/sources/${sourceId}/articles`, { method: 'POST', body: JSON.stringify(body) }),
  getArticle: (id: string) => apiFetch<KnowledgeArticle>(`/v1/knowledge/articles/${id}`),
  updateArticle: (id: string, body: object) =>
    apiFetch<KnowledgeArticle>(`/v1/knowledge/articles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteArticle: (id: string) => apiFetch<void>(`/v1/knowledge/articles/${id}`, { method: 'DELETE' }),
  publish: (id: string) => apiFetch<{ message: string }>(`/v1/knowledge/articles/${id}/publish`, { method: 'POST' }),
  unpublish: (id: string) => apiFetch<KnowledgeArticle>(`/v1/knowledge/articles/${id}/unpublish`, { method: 'POST' }),
  getChunks: (id: string) => apiFetch<unknown[]>(`/v1/knowledge/articles/${id}/chunks`),
  search: (body: { query: string; sourceId?: string; country?: string; language?: string; topN?: number }) =>
    apiFetch<unknown[]>('/v1/knowledge/search', { method: 'POST', body: JSON.stringify(body) }),

  // ── Topics ──
  listTopics: (sourceId: string) =>
    apiFetch<KnowledgeTopic[]>(`/v1/knowledge/sources/${sourceId}/topics`),
  createTopic: (sourceId: string, body: { name: string; parentId?: string }) =>
    apiFetch<KnowledgeTopic>(`/v1/knowledge/sources/${sourceId}/topics`, { method: 'POST', body: JSON.stringify(body) }),
  updateTopic: (topicId: string, body: { name?: string; parentId?: string | null }) =>
    apiFetch<KnowledgeTopic>(`/v1/knowledge/topics/${topicId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteTopic: (topicId: string) =>
    apiFetch<void>(`/v1/knowledge/topics/${topicId}`, { method: 'DELETE' }),
};

// ── Phase 3: Approvals ────────────────────────────────

export const approvalApi = {
  list: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return apiFetch<ApprovalRequest[]>(`/v1/approvals${q}`);
  },
  get: (id: string) => apiFetch<ApprovalRequest>(`/v1/approvals/${id}`),
  approve: (id: string) => apiFetch<ApprovalRequest>(`/v1/approvals/${id}/approve`, { method: 'POST' }),
  reject: (id: string, reason?: string) =>
    apiFetch<ApprovalRequest>(`/v1/approvals/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
};

// ── Phase 3: Guardrails ───────────────────────────────

export const guardrailApi = {
  list: () => apiFetch<Guardrail[]>('/v1/guardrails'),
  create: (body: Partial<Guardrail>) => apiFetch<Guardrail>('/v1/guardrails', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Guardrail>) =>
    apiFetch<Guardrail>(`/v1/guardrails/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/guardrails/${id}`, { method: 'DELETE' }),
};

// ── Alerts ───────────────────────────────────────────

export interface AlertItem {
  id: string;
  tenantId: string | null;
  severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  status: 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';
  source: string;
  title: string;
  message: string;
  resolutionHint: string | null;
  context: Record<string, unknown>;
  resolvedAt: string | null;
  createdAt: string;
}

export const alertApi = {
  list: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return apiFetch<AlertItem[]>(`/v1/settings/alerts${q}`);
  },
  acknowledge: (id: string) => apiFetch<AlertItem>(`/v1/settings/alerts/${id}/acknowledge`, { method: 'POST' }),
  resolve: (id: string) => apiFetch<AlertItem>(`/v1/settings/alerts/${id}/resolve`, { method: 'POST' }),
};

// ── Voice Numbers ──────────────────────────────────────────────────────

export interface VoicePhoneNumber {
  id: string;
  tenantId: string;
  phoneNumber: string;
  displayName: string;
  agentId: string | null;
  agent: { id: string; name: string } | null;
  language: string;
  ttsVoice: string;
  welcomeMessage: string;
  noInputMessage: string;
  handoffPhoneNumber: string | null;
  handoffMessage: string;
  maxAiTurns: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CreateVoicePhoneNumberDto = {
  phoneNumber: string;
  displayName: string;
  agentId?: string;
  language?: string;
  ttsVoice?: string;
  welcomeMessage: string;
  noInputMessage?: string;
  handoffPhoneNumber?: string;
  handoffMessage?: string;
  maxAiTurns?: number;
  active?: boolean;
};

export const voiceNumberApi = {
  list: () => apiFetch<VoicePhoneNumber[]>('/v1/settings/voice-numbers'),
  get: (id: string) => apiFetch<VoicePhoneNumber>(`/v1/settings/voice-numbers/${id}`),
  create: (dto: CreateVoicePhoneNumberDto) =>
    apiFetch<VoicePhoneNumber>('/v1/settings/voice-numbers', {
      method: 'POST',
      body: JSON.stringify(dto),
    }),
  update: (id: string, dto: Partial<CreateVoicePhoneNumberDto>) =>
    apiFetch<VoicePhoneNumber>(`/v1/settings/voice-numbers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),
  delete: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/v1/settings/voice-numbers/${id}`, { method: 'DELETE' }),
};

// ── Phase 12: Operator Teams ──────────────────────────

export type TeamRole = 'MEMBER' | 'LEAD';
export type HitlStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'TIMED_OUT';

export interface OperatorTeam {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  color: string | null;
  trafficThreshold: number;
  createdAt: string;
  updatedAt: string;
  members?: OperatorTeamMember[];
  capabilities?: TeamApprovalCapability[];
  _count?: { members: number; conversations: number; hitlRequests: number };
}

export interface OperatorTeamMember {
  id: string;
  teamId: string;
  userId: string;
  role: TeamRole;
  joinedAt: string;
  user?: { id: string; name: string | null; email: string };
}

export interface TeamApprovalCapability {
  id: string;
  teamId: string;
  capability: string;
  maxValue: number | null;
  createdAt: string;
}

export interface HitlDecisionRequest {
  id: string;
  tenantId: string;
  conversationId: string;
  teamId: string | null;
  capability: string;
  question: string;
  context: Record<string, unknown>;
  status: HitlStatus;
  decisionValue: string | null;
  decisionNote: string | null;
  decidedByUserId: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  team?: { id: string; name: string; color: string | null } | null;
  decidedByUser?: { id: string; name: string | null; email: string } | null;
}

export interface TeamTrafficSummary {
  teamId: string;
  teamName: string;
  color: string | null;
  openConversations: number;
  threshold: number;
  isHighTraffic: boolean;
  loadPercent: number;
  pendingHitl: number;
}

export const teamApi = {
  list: () => apiFetch<OperatorTeam[]>('/v1/teams'),
  get: (id: string) => apiFetch<OperatorTeam>(`/v1/teams/${id}`),
  create: (body: { name: string; description?: string; color?: string; trafficThreshold?: number }) =>
    apiFetch<OperatorTeam>('/v1/teams', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<{ name: string; description: string; color: string; trafficThreshold: number }>) =>
    apiFetch<OperatorTeam>(`/v1/teams/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/teams/${id}`, { method: 'DELETE' }),

  addMember: (teamId: string, userId: string, role?: TeamRole) =>
    apiFetch<OperatorTeamMember>(`/v1/teams/${teamId}/members`, { method: 'POST', body: JSON.stringify({ userId, role }) }),
  removeMember: (teamId: string, userId: string) =>
    apiFetch<void>(`/v1/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),

  addCapability: (teamId: string, capability: string, maxValue?: number) =>
    apiFetch<TeamApprovalCapability>(`/v1/teams/${teamId}/capabilities`, { method: 'POST', body: JSON.stringify({ capability, maxValue }) }),
  removeCapability: (teamId: string, capability: string) =>
    apiFetch<void>(`/v1/teams/${teamId}/capabilities/${capability}`, { method: 'DELETE' }),

  assignConversation: (teamId: string, conversationId: string) =>
    apiFetch<void>(`/v1/conversations/${conversationId}/teams`, { method: 'POST', body: JSON.stringify({ teamId }) }),
  removeConversation: (teamId: string, conversationId: string) =>
    apiFetch<void>(`/v1/conversations/${conversationId}/teams/${teamId}`, { method: 'DELETE' }),
};

export const hitlApi = {
  list: (params?: { status?: HitlStatus; teamId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.teamId) qs.set('teamId', params.teamId);
    const q = qs.toString();
    return apiFetch<HitlDecisionRequest[]>(`/v1/hitl${q ? `?${q}` : ''}`);
  },
  get: (id: string) => apiFetch<HitlDecisionRequest>(`/v1/hitl/${id}`),
  decide: (id: string, body: { decision: 'APPROVED' | 'REJECTED'; value?: string; note?: string }) =>
    apiFetch<HitlDecisionRequest>(`/v1/hitl/${id}/decide`, { method: 'POST', body: JSON.stringify(body) }),
};

export const monitorApi = {
  traffic: () => apiFetch<TeamTrafficSummary[]>('/v1/monitor/traffic'),
  conversations: (params?: { teamId?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.teamId) qs.set('teamId', params.teamId);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return apiFetch<Conversation[]>(`/v1/monitor/conversations${q ? `?${q}` : ''}`);
  },
};

// ── Secrets ───────────────────────────────────────────

export interface TenantSecret {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export const secretApi = {
  list: () => apiFetch<TenantSecret[]>('/v1/secrets'),
  create: (data: { name: string; value: string; description?: string }) =>
    apiFetch<TenantSecret>('/v1/secrets', { method: 'POST', body: JSON.stringify(data) }),
  update: (name: string, data: { value?: string; description?: string }) =>
    apiFetch<TenantSecret>(`/v1/secrets/${name}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (name: string) =>
    apiFetch<void>(`/v1/secrets/${name}`, { method: 'DELETE' }),
};

// ── Analytics ─────────────────────────────────────────

export interface OverviewStats {
  totalConversations: number;
  totalMessages: number;
  resolutionRate: number;
  avgTurns: number;
  avgDurationSeconds: number;
  totalTokens: number;
}
export interface VolumeStat  { day: string; channel: string; count: number; }
export interface SkillStat   { day: string; skillId: string; count: number; }
export interface CostStat    { day: string; totalTokens: number; estimatedCost: number; }
export interface LatencyStats { p50: number; p95: number; }
export interface HandoffFunnel {
  totalConversations: number;
  handoffsCount: number;
  handoffRate: number;
}

export const analyticsApi = {
  getOverview: (range?: string) => apiFetch<OverviewStats>(`/v1/analytics/overview${range ? `?range=${range}` : ''}`),
  getVolume:   (range?: string) => apiFetch<VolumeStat[]>(`/v1/analytics/volume${range ? `?range=${range}` : ''}`),
  getSkills:   (range?: string) => apiFetch<SkillStat[]>(`/v1/analytics/skills${range ? `?range=${range}` : ''}`),
  getCosts:    (range?: string) => apiFetch<CostStat[]>(`/v1/analytics/costs${range ? `?range=${range}` : ''}`),
  getLatency:  (range?: string) => apiFetch<LatencyStats>(`/v1/analytics/latency${range ? `?range=${range}` : ''}`),
  getHandoffs: (range?: string) => apiFetch<HandoffFunnel>(`/v1/analytics/handoffs${range ? `?range=${range}` : ''}`),
};

// ── Workflows ─────────────────────────────────────────

export type WorkflowStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'WAITING_CALLBACK' | 'WAITING_APPROVAL';
export type WorkflowStepType =
  | 'TOOL_CALL' | 'CONDITION' | 'DELAY' | 'WAIT_CALLBACK' | 'MESSAGE'
  | 'tool' | 'skill' | 'wait' | 'condition' | 'callback';

export interface WorkflowStep {
  id: string;
  type: WorkflowStepType;
  name: string;
  config: Record<string, unknown>;
  nextStepId?: string;
  errorStepId?: string;
  // convenience fields used by the step builder (stored in config at runtime)
  [key: string]: unknown;
}

export interface WorkflowDefinition {
  id: string;
  tenantId: string;
  agentId: string;
  name: string;
  description?: string;
  steps: WorkflowStep[];
  triggerConfig?: any;
  abortListeners?: any[];
  active: boolean;
  version: number;
  maxExecutionTime?: number | null;
  createdAt: string;
  updatedAt: string;
  agent?: { id: string; name: string };
  executions?: WorkflowExecution[];
  _count?: { executions: number };
}

export interface WorkflowExecution {
  id: string;
  tenantId: string;
  definitionId: string;
  conversationId?: string;
  triggerExecutionId?: string;
  status: WorkflowStatus;
  currentStepIndex: number;
  state: Record<string, unknown>;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  activeDurationSec?: number;
  functionDurationSec?: number;
  tokensUsedByModel?: Record<string, number>;
  maxExecutionTime?: number | null;
  createdAt: string;
  updatedAt: string;
  definition?: Pick<WorkflowDefinition, 'id' | 'name' | 'steps'>;
}

export const workflowApi = {
  list: () => apiFetch<WorkflowDefinition[]>('/v1/workflows'),
  get: (id: string) => apiFetch<WorkflowDefinition>(`/v1/workflows/${id}`),
  create: (body: Partial<WorkflowDefinition>) =>
    apiFetch<WorkflowDefinition>('/v1/workflows', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<WorkflowDefinition>) =>
    apiFetch<WorkflowDefinition>(`/v1/workflows/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/workflows/${id}`, { method: 'DELETE' }),
  execute: (id: string, body?: { conversationId?: string; initialState?: Record<string, unknown> }) =>
    apiFetch<WorkflowExecution>(`/v1/workflows/${id}/execute`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  getExecution: (executionId: string) =>
    apiFetch<WorkflowExecution>(`/v1/workflows/executions/${executionId}`),
  cancelExecution: (executionId: string) =>
    apiFetch<WorkflowExecution>(`/v1/workflows/executions/${executionId}/cancel`, { method: 'POST' }),
  resumeExecution: (executionId: string, data: unknown) =>
    apiFetch<WorkflowExecution>(`/v1/workflows/executions/${executionId}/callback`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    }),
};

// ── Triggers ──────────────────────────────────────────

export type TriggerType = 'SCHEDULED' | 'EVENT' | 'WEBHOOK';
export type TriggerExecutionStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface TriggerDefinition {
  id: string;
  tenantId: string;
  agentId: string;
  name: string;
  description?: string;
  triggerType: TriggerType;
  config: Record<string, unknown>;
  active: boolean;
  createdAt: string;
  agent?: { id: string; name: string };
  executions?: TriggerExecution[];
  _count?: { executions: number };
}

export interface TriggerExecution {
  id: string;
  triggerId: string;
  status: TriggerExecutionStatus;
  conversationId?: string;
  payload?: Record<string, unknown>;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

export const triggerApi = {
  list: () => apiFetch<TriggerDefinition[]>('/v1/triggers'),
  get: (id: string) => apiFetch<TriggerDefinition>(`/v1/triggers/${id}`),
  create: (body: Partial<TriggerDefinition>) =>
    apiFetch<TriggerDefinition>('/v1/triggers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<TriggerDefinition>) =>
    apiFetch<TriggerDefinition>(`/v1/triggers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/triggers/${id}`, { method: 'DELETE' }),
  executions: (id: string) => apiFetch<TriggerExecution[]>(`/v1/triggers/${id}/executions`),
};

// ── Event Connectors ──────────────────────────────────

export type EventConnectorProvider =
  | 'GOOGLE_PUBSUB' | 'KAFKA' | 'AWS_SQS' | 'AWS_SNS'
  | 'AWS_EVENTBRIDGE' | 'AZURE_SERVICE_BUS' | 'WEBHOOK';

export type EventConnectorDirection = 'INBOUND' | 'OUTBOUND' | 'BIDIRECTIONAL';

export interface EventConnectorWorkflowLink {
  id: string;
  workflowId: string;
  eventFilter?: string | null;
  messageTemplate?: string | null;
  workflow?: { id: string; name: string };
}

export interface EventConnector {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  provider: EventConnectorProvider;
  direction: EventConnectorDirection;
  config: Record<string, unknown>;
  schema?: Record<string, unknown> | null;
  active: boolean;
  webhookSecret?: string | null;
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
  workflowLinks?: EventConnectorWorkflowLink[];
  _count?: { workflowLinks: number };
}

export const eventConnectorApi = {
  list: () => apiFetch<EventConnector[]>('/v1/event-connectors'),
  get: (id: string) => apiFetch<EventConnector>(`/v1/event-connectors/${id}`),
  create: (body: {
    name: string;
    description?: string;
    provider: EventConnectorProvider;
    direction?: EventConnectorDirection;
    config?: Record<string, unknown>;
    schema?: Record<string, unknown> | null;
    active?: boolean;
  }) => apiFetch<EventConnector>('/v1/event-connectors', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<{
    name: string;
    description: string;
    provider: EventConnectorProvider;
    direction: EventConnectorDirection;
    config: Record<string, unknown>;
    schema: Record<string, unknown> | null;
    active: boolean;
  }>) => apiFetch<EventConnector>(`/v1/event-connectors/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/event-connectors/${id}`, { method: 'DELETE' }),

  linkWorkflow: (id: string, body: { workflowId: string; eventFilter?: string; messageTemplate?: string }) =>
    apiFetch<EventConnectorWorkflowLink>(`/v1/event-connectors/${id}/workflows`, { method: 'POST', body: JSON.stringify(body) }),
  unlinkWorkflow: (id: string, workflowId: string) =>
    apiFetch<void>(`/v1/event-connectors/${id}/workflows/${workflowId}`, { method: 'DELETE' }),

  publish: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ published: boolean; messageId?: string }>(`/v1/event-connectors/${id}/publish`, { method: 'POST', body: JSON.stringify({ payload }) }),
  test: (id: string, payload?: Record<string, unknown>) =>
    apiFetch<{ connectorId: string; inbound?: { success: boolean; workflowsTriggered: number }; outbound?: { success: boolean; messageId?: string } }>(
      `/v1/event-connectors/${id}/test`, { method: 'POST', body: JSON.stringify({ payload }) }
    ),
};


// ── Audit Log ─────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  tenantId: string | null;
  actorId: string | null;
  actorEmail: string;
  actorRole: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  changes: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  impersonatedBy: string | null;
  createdAt: string;
}

export interface AuditLogPage {
  total: number;
  page: number;
  limit: number;
  pages: number;
  entries: AuditLogEntry[];
}

export const auditLogApi = {
  list: (params?: {
    resourceType?: string;
    action?: string;
    actorId?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.resourceType) qs.set('resourceType', params.resourceType);
    if (params?.action) qs.set('action', params.action);
    if (params?.actorId) qs.set('actorId', params.actorId);
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return apiFetch<AuditLogPage>(`/v1/audit-log${q ? `?${q}` : ''}`);
  },
  export: (from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const q = qs.toString();
    return apiFetch<string>(`/v1/settings/audit-log/export${q ? `?${q}` : ''}`, { asText: true });
  },
};

// ── Email Connectors ──────────────────────────────────

export type EmailConnectorProvider = 'GMAIL' | 'OUTLOOK' | 'IMAP_SMTP';

export interface EmailConnectorWorkflowLink {
  id: string;
  workflowId: string;
  emailFilter?: string | null;
  messageTemplate?: string | null;
  workflow?: { id: string; name: string; active: boolean };
}

export interface EmailConnector {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  provider: EmailConnectorProvider;
  config: Record<string, unknown>;
  active: boolean;
  connectedEmail?: string | null;
  consumerStatus: string;
  webhookSecret?: string | null;
  lastEmailAt?: string | null;
  emailCount: number;
  createdAt: string;
  updatedAt: string;
  workflows?: EmailConnectorWorkflowLink[];
  _count?: { workflows: number };
}

export const emailConnectorApi = {
  list: () => apiFetch<EmailConnector[]>('/v1/email-connectors'),
  get: (id: string) => apiFetch<EmailConnector>(`/v1/email-connectors/${id}`),
  create: (body: {
    name: string;
    description?: string;
    provider: EmailConnectorProvider;
    active?: boolean;
  }) => apiFetch<EmailConnector>('/v1/email-connectors', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<{ name: string; description: string; active: boolean }>) =>
    apiFetch<EmailConnector>(`/v1/email-connectors/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/email-connectors/${id}`, { method: 'DELETE' }),

  connectImap: (body: {
    connectorId: string;
    imapHost: string; imapPort: number; imapSecure: boolean;
    smtpHost: string; smtpPort: number; smtpSecure: boolean;
    email: string; password: string;
  }) => apiFetch<EmailConnector>('/v1/email-connectors/imap/connect', { method: 'POST', body: JSON.stringify(body) }),

  getGmailAuthUrl: (connectorId: string, clientId: string, redirectUri: string) =>
    apiFetch<{ url: string }>(`/v1/email-connectors/gmail/auth-url?connectorId=${connectorId}&clientId=${encodeURIComponent(clientId)}&redirectUri=${encodeURIComponent(redirectUri)}`),

  getOutlookAuthUrl: (connectorId: string, clientId: string, directoryTenantId: string, redirectUri: string) =>
    apiFetch<{ url: string }>(`/v1/email-connectors/outlook/auth-url?connectorId=${connectorId}&clientId=${encodeURIComponent(clientId)}&directoryTenantId=${encodeURIComponent(directoryTenantId)}&redirectUri=${encodeURIComponent(redirectUri)}`),

  linkWorkflow: (id: string, body: { workflowId: string; emailFilter?: string; messageTemplate?: string }) =>
    apiFetch<EmailConnectorWorkflowLink>(`/v1/email-connectors/${id}/workflows`, { method: 'POST', body: JSON.stringify(body) }),
  unlinkWorkflow: (id: string, workflowId: string) =>
    apiFetch<void>(`/v1/email-connectors/${id}/workflows/${workflowId}`, { method: 'DELETE' }),
};


// ─── Usage & Billing API ──────────────────────────────────────────────────────

export interface UsageBalance {
  billingMode: 'PREPAID' | 'POSTPAID';
  creditBalance: number;
  billingStatus: 'ACTIVE' | 'LOW_CREDITS' | 'BLOCKED';
  usageAccruedCredits: number;
  billingCycleAnchorDay: number;
  lastInvoicedAt: string | null;
  autoRechargeEnabled: boolean;
  autoRechargeThresholdCredits: number;
  autoRechargeAmountCredits: number;
}

export interface LlmUsageSummary {
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  avgDurationMs: number;
  totalProviderCostUsd: number | null;
  totalCreditCost: number | null;
  topModel: { provider: string; modelName: string; calls: number } | null;
}

export interface ComputeUsageSummary {
  totalWorkflows: number;
  totalActiveSec: number;
  totalFunctionSec: number;
  totalCreditCost: number | null;
}

export interface DailyLedgerRow {
  day: string;
  creditsUsed: number;
  creditsBought: number;
  creditsInvoiced: number;
  creditsAdjusted: number;
  netChange: number;
  entryCount: number;
}

export interface LedgerEntry {
  id: string;
  entryType: 'USAGE_DEBIT' | 'TOPUP_CREDIT' | 'INVOICE_DEBIT' | 'ADJUSTMENT';
  amount: number;
  description: string;
  sourceType: string | null;
  sourceId: string | null;
  balanceAfter: number | null;
  createdAt: string;
}

export interface ModelBreakdownRow {
  provider: string;
  modelName: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  providerCostUsd: number | null;
  creditCost: number | null;
}

export type UsagePeriod = 'hour' | 'today' | 'week' | 'month' | '30d';

export const usageApi = {
  getBalance: () => apiFetch<UsageBalance>('/v1/analytics/usage/balance'),
  getLlmSummary: (period: UsagePeriod = 'today') => apiFetch<LlmUsageSummary>(`/v1/analytics/usage/llm-summary?period=${period}`),
  getComputeSummary: (period: UsagePeriod = 'today') => apiFetch<ComputeUsageSummary>(`/v1/analytics/usage/compute-summary?period=${period}`),
  getDailyReport: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch<DailyLedgerRow[]>(`/v1/analytics/usage/ledger/daily?${params}`);
  },
  getRecentEntries: (limit = 50, offset = 0) =>
    apiFetch<LedgerEntry[]>(`/v1/analytics/usage/ledger/entries?limit=${limit}&offset=${offset}`),
  getModelBreakdown: (period: UsagePeriod = 'today') =>
    apiFetch<ModelBreakdownRow[]>(`/v1/analytics/usage/model-breakdown?period=${period}`),
  getMemorySummary: (period: UsagePeriod = 'today') =>
    apiFetch<MemorySummary>(`/v1/analytics/usage/memory-summary?period=${period}`),
  getMemoryStorage: () =>
    apiFetch<MemoryStorage>('/v1/analytics/usage/memory-storage'),
};

// ─── Invoice API ──────────────────────────────────────────────────────────────

export interface InvoiceLineItem {
  description: string;
  credits: number;
  amountUsd: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceType: 'POSTPAID_USAGE' | 'PREPAID_TOPUP';
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID';
  periodStart: string | null;
  periodEnd: string | null;
  totalCredits: number;
  totalAmountUsd: number;
  lineItems: InvoiceLineItem[];
  stripeInvoiceId: string | null;
  paidAt: string | null;
  dueAt: string | null;
  createdAt: string;
}

export const invoiceApi = {
  list: (params?: { status?: string; invoiceType?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.status)      q.set('status',      params.status);
    if (params?.invoiceType) q.set('invoiceType',  params.invoiceType);
    if (params?.limit)       q.set('limit',        String(params.limit));
    if (params?.offset)      q.set('offset',       String(params.offset));
    return apiFetch<Invoice[]>(`/v1/billing/invoices?${q}`);
  },
  get: (id: string) => apiFetch<Invoice>(`/v1/billing/invoices/${id}`),
  generatePostpaid: () => apiFetch<Invoice | null>('/v1/billing/invoices/generate', { method: 'POST' }),
};

// ─── Memory Summary API ───────────────────────────────────────────────────────

export interface MemorySummary {
  shortTermCredits:   number | null;
  longTermCredits:    number | null;
  memoryWriteCredits: number | null;
  totalMemoryCredits: number | null;
  shortTermEvents:    number;
  longTermEvents:     number;
  memoryWriteEvents:  number;
}

export interface MemoryStorage {
  storageMb:      number;
  hourlyCredits:  number;
  dailyCredits:   number;
  dailyUsd:       number;
  pricePerMbDay:  number;
}

// ─── Users (Invitations) ─────────────────────────────────────────────────────

export type UserRole = 'ADMIN' | 'DEVELOPER' | 'OPERATOR' | 'VIEWER';
export type EmailProvider = 'GOOGLE' | 'MICROSOFT' | 'UNKNOWN';

export interface InvitedUser {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface InviteResult {
  user: InvitedUser;
  detectedProvider: EmailProvider;
}

export const userApi = {
  list: () =>
    apiFetch<InvitedUser[]>('/v1/users'),

  detectProvider: (email: string) =>
    apiFetch<{ provider: EmailProvider }>(`/v1/users/detect-provider?email=${encodeURIComponent(email)}`),

  invite: (data: { email: string; role: UserRole; name?: string }) =>
    apiFetch<InviteResult>('/v1/users', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: { name?: string }) =>
    apiFetch<InvitedUser>(`/v1/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  updateRole: (id: string, role: UserRole) =>
    apiFetch<InvitedUser>(`/v1/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),

  remove: (id: string) =>
    apiFetch<void>(`/v1/users/${id}`, { method: 'DELETE' }),
};
